import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calculator, Package, Send, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to extract percentage from pricing_fields array
const getFieldPercent = (template, key) => {
  if (!template?.pricing_fields) return 0;
  const field = template.pricing_fields.find(f => f.key === key);
  return field?.percent ?? 0;
};

export default function CostCardCreatePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const partNumber = searchParams.get('part');

  const [part, setPart] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [customsDuty, setCustomsDuty] = useState(0);
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (partNumber) {
      fetchData();
    }
  }, [partNumber]);

  const fetchData = async () => {
    try {
      const [partRes, templatesRes, currenciesRes] = await Promise.all([
        axios.get(`${API}/parts/${partNumber}`),
        axios.get(`${API}/templates`),
        axios.get(`${API}/currency-rates`)
      ]);
      
      setPart(partRes.data);
      setTemplates(templatesRes.data);
      setCurrencies(currenciesRes.data);
      
      // Auto-select first template
      if (templatesRes.data.length > 0) {
        setSelectedTemplate(templatesRes.data[0].id);
      }
      
      // Set currency based on vendor code (simplified logic)
      const vendorCode = partRes.data.vendor_code || '';
      if (vendorCode.includes('EU')) setCurrency('EUR');
      else if (vendorCode.includes('US')) setCurrency('USD');
      else setCurrency('INR');
      
    } catch (error) {
      toast.error('Failed to load data');
      navigate('/parts');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a cost template');
      return;
    }

    const template = templates.find(t => t.id === selectedTemplate);
    if (!template) return;

    setCalculating(true);
    try {
      // Extract percentages from the dynamic pricing_fields array
      const iscPercent = getFieldPercent(template, 'isc');
      const gpPercent = getFieldPercent(template, 'gp');
      const ddrPercent = getFieldPercent(template, 'ddr');
      
      const response = await axios.post(`${API}/pricing/calculate`, {
        purchase_price: part.sap_purchase_price,
        currency_code: currency,
        isc_percent: iscPercent,
        customs_duty_percent: customsDuty,
        gp_percent: gpPercent,
        ddr_percent: ddrPercent
      });
      
      setCalculation(response.data);
      toast.success('Pricing calculated successfully!');
    } catch (error) {
      toast.error('Failed to calculate pricing');
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async () => {
    if (!calculation) {
      toast.error('Please calculate pricing first');
      return;
    }

    const template = templates.find(t => t.id === selectedTemplate);
    
    setSubmitting(true);
    try {
      // Extract percentages from the dynamic pricing_fields array
      const iscPercent = getFieldPercent(template, 'isc');
      const gpPercent = getFieldPercent(template, 'gp');
      const ddrPercent = getFieldPercent(template, 'ddr');
      
      await axios.post(`${API}/cost-cards`, {
        part_number: part.part_number,
        template_id: selectedTemplate,
        currency: currency,
        customs_duty_percent: customsDuty,
        purchase_price: part.sap_purchase_price,
        ...calculation,
        isc_percent: iscPercent,
        gp_percent: gpPercent,
        ddr_percent: ddrPercent
      });
      
      toast.success('COGS card created successfully!');
      navigate(`/cogs-cards`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create COGS card');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C3002F] mx-auto mb-4"></div>
            <p className="text-zinc-600 dark:text-zinc-400">Loading...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const template = templates.find(t => t.id === selectedTemplate);

  return (
    <MainLayout>
      <div className="p-8 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-zinc-900 dark:text-white tracking-tight">Create COGS Card</h1>
            <p className="text-zinc-500 dark:text-zinc-400 mt-1">Calculate pricing for {part?.part_number}</p>
          </div>
        </div>

        {/* Part Information */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Package className="w-5 h-5 text-[#C3002F]" />
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Part Information</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm text-zinc-500">Part Number</Label>
              <p className="font-mono font-semibold text-zinc-900 dark:text-white">{part?.part_number}</p>
            </div>
            <div>
              <Label className="text-sm text-zinc-500">Part Name</Label>
              <p className="text-zinc-900 dark:text-white">{part?.name}</p>
            </div>
            <div>
              <Label className="text-sm text-zinc-500">Purchase Price (SAP)</Label>
              <p className="font-mono font-semibold text-[#C3002F]">{currency} {part?.sap_purchase_price?.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Configuration */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <Calculator className="w-5 h-5 text-[#C3002F]" />
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Pricing Configuration</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Template Selection */}
            <div>
              <Label>Cost Template</Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger>
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {template && (
                <div className="mt-2 text-xs text-zinc-500 space-y-1">
                  <p>ISC: {getFieldPercent(template, 'isc')}%</p>
                  <p>GP: {getFieldPercent(template, 'gp')}%</p>
                  <p>DDR: {getFieldPercent(template, 'ddr')}%</p>
                </div>
              )}
            </div>

            {/* Currency */}
            <div>
              <Label>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map(c => (
                    <SelectItem key={c.currency_code} value={c.currency_code}>
                      {c.currency_code} ({c.symbol}) - ₹{c.exchange_rate}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Customs Duty */}
            <div>
              <Label>Customs Duty (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={customsDuty}
                onChange={(e) => setCustomsDuty(parseFloat(e.target.value) || 0)}
                placeholder="0.0"
              />
            </div>
          </div>

          <div className="mt-6">
            <Button 
              onClick={handleCalculate} 
              disabled={calculating || !selectedTemplate}
              className="bg-[#C3002F] hover:bg-[#A3002A]"
            >
              {calculating ? 'Calculating...' : 'Calculate Pricing'}
              <Calculator className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Calculation Results - Excel Layout */}
        {calculation && (
          <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 dark:from-emerald-950/20 dark:to-cyan-950/20 border-2 border-emerald-200 dark:border-emerald-800 rounded-lg p-6">
            <h2 className="text-xl font-bold text-zinc-900 dark:text-white mb-6 flex items-center gap-2">
              <Calculator className="w-6 h-6 text-emerald-600" />
              Pricing Calculation Results
            </h2>

            {/* Excel-Style Flow */}
            <div className="space-y-4">
              {/* Row 1: Inputs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white dark:bg-zinc-900 p-3 sm:p-4 rounded border border-zinc-200 dark:border-zinc-800">
                  <p className="text-xs text-zinc-500 uppercase mb-1">PP</p>
                  <p className="text-base sm:text-lg font-mono font-bold text-zinc-900 dark:text-white">
                    ₹{calculation.purchase_price_inr?.toLocaleString()}
                  </p>
                </div>
                <div className="bg-white dark:bg-zinc-900 p-3 sm:p-4 rounded border border-zinc-200 dark:border-zinc-800">
                  <p className="text-xs text-zinc-500 uppercase mb-1">Currency</p>
                  <p className="text-base sm:text-lg font-semibold text-zinc-900 dark:text-white">{currency}</p>
                </div>
                <div className="bg-white dark:bg-zinc-900 p-3 sm:p-4 rounded border border-zinc-200 dark:border-zinc-800">
                  <p className="text-xs text-zinc-500 uppercase mb-1">Custom Duty</p>
                  <p className="text-base sm:text-lg font-mono font-bold text-zinc-900 dark:text-white">
                    ₹{calculation.customs_amount?.toLocaleString()}
                  </p>
                </div>
                <div className="bg-white dark:bg-zinc-900 p-3 sm:p-4 rounded border border-zinc-200 dark:border-zinc-800">
                  <p className="text-xs text-zinc-500 uppercase mb-1">ISC @ {getFieldPercent(template, 'isc')}%</p>
                  <p className="text-base sm:text-lg font-mono font-bold text-zinc-900 dark:text-white">
                    ₹{calculation.isc_amount?.toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-center">
                <ArrowRight className="w-5 h-5 text-zinc-400" />
              </div>

              {/* Row 2: COGS to DBP */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-cyan-50 dark:bg-cyan-950/30 p-3 sm:p-4 rounded border-2 border-cyan-300 dark:border-cyan-700">
                  <p className="text-xs text-cyan-700 dark:text-cyan-400 uppercase mb-1 font-semibold">COGS</p>
                  <p className="text-lg sm:text-xl font-mono font-bold text-cyan-900 dark:text-cyan-300">
                    ₹{calculation.cogs?.toLocaleString()}
                  </p>
                </div>
                <div className="bg-white dark:bg-zinc-900 p-3 sm:p-4 rounded border border-zinc-200 dark:border-zinc-800">
                  <p className="text-xs text-zinc-500 uppercase mb-1">GP%</p>
                  <p className="text-base sm:text-lg font-mono font-bold text-zinc-900 dark:text-white">
                    {calculation.gp_percent}%
                  </p>
                </div>
                <div className="bg-violet-50 dark:bg-violet-950/30 p-3 sm:p-4 rounded border-2 border-violet-300 dark:border-violet-700">
                  <p className="text-xs text-violet-700 dark:text-violet-400 uppercase mb-1 font-semibold">DBP</p>
                  <p className="text-lg sm:text-xl font-mono font-bold text-violet-900 dark:text-violet-300">
                    ₹{calculation.dbp?.toLocaleString()}
                  </p>
                </div>
                <div className="bg-white dark:bg-zinc-900 p-3 sm:p-4 rounded border border-zinc-200 dark:border-zinc-800">
                  <p className="text-xs text-zinc-500 uppercase mb-1">GP</p>
                  <p className="text-base sm:text-lg font-mono font-bold text-zinc-900 dark:text-white">
                    ₹{calculation.gp_amount?.toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-center">
                <ArrowRight className="w-5 h-5 text-zinc-400" />
              </div>

              {/* Row 3: Final Prices */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <div className="bg-white dark:bg-zinc-900 p-3 sm:p-4 rounded border border-zinc-200 dark:border-zinc-800">
                  <p className="text-xs text-zinc-500 uppercase mb-1">DDR%</p>
                  <p className="text-base sm:text-lg font-mono font-bold text-zinc-900 dark:text-white">
                    {calculation.ddr_percent}%
                  </p>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-950/30 p-3 sm:p-4 rounded border-2 border-emerald-300 dark:border-emerald-700">
                  <p className="text-xs text-emerald-700 dark:text-emerald-400 uppercase mb-1 font-semibold">MRP</p>
                  <p className="text-lg sm:text-xl font-mono font-bold text-emerald-900 dark:text-emerald-300">
                    ₹{calculation.mrp?.toLocaleString()}
                  </p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-1">Incl. GST</p>
                </div>
                <div className="bg-amber-50 dark:bg-amber-950/30 p-3 sm:p-4 rounded border-2 border-amber-300 dark:border-amber-700">
                  <p className="text-xs text-amber-700 dark:text-amber-400 uppercase mb-1 font-semibold">CBP</p>
                  <p className="text-lg sm:text-xl font-mono font-bold text-amber-900 dark:text-amber-300">
                    ₹{calculation.cbp?.toLocaleString()}
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">Excl. GST</p>
                </div>
                <div className="bg-white dark:bg-zinc-900 p-3 sm:p-4 rounded border border-zinc-200 dark:border-zinc-800">
                  <p className="text-xs text-zinc-500 uppercase mb-1">Dealer Margin</p>
                  <p className="text-base sm:text-lg font-mono font-bold text-zinc-900 dark:text-white">
                    ₹{calculation.dealer_margin?.toLocaleString()}
                  </p>
                </div>
                <div className="bg-white dark:bg-zinc-900 p-3 sm:p-4 rounded border border-zinc-200 dark:border-zinc-800">
                  <p className="text-xs text-zinc-500 uppercase mb-1">Margin %</p>
                  <p className="text-base sm:text-lg font-mono font-bold text-zinc-900 dark:text-white">
                    {calculation.dealer_margin_percent}%
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8 flex gap-3">
              <Button 
                onClick={handleSubmit} 
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                size="lg"
              >
                {submitting ? 'Submitting...' : 'Create COGS Card'}
                <Send className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                onClick={() => navigate('/parts')} 
                variant="outline"
                size="lg"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
